import { useState } from "react";
import { Share2, Check, Copy, Link2, Linkedin, Loader2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { getReviewTypeConfig, type Review } from "@/lib/mockData";

interface Props {
  review: Review;
}


const ratingWord = (r: number) => {
  if (r >= 4.5) return "an awesome";
  if (r >= 4) return "a great";
  if (r >= 3) return "a solid";
  if (r >= 2) return "a mixed";
  return "a rough";
};

const difficultyWord = (d: number) => {
  if (d >= 4.5) return "extremely demanding";
  if (d >= 3.6) return "pretty demanding";
  if (d >= 2.5) return "moderately challenging";
  if (d >= 1.5) return "fairly manageable";
  return "very light";
};

const monthYear = (d?: string | null) =>
  d ? new Date(d).toLocaleDateString("en-US", { month: "short", year: "numeric" }) : null;

const reviewUrl = (review: Review) =>
  typeof window === "undefined" ? "" : `${window.location.origin}/review/${review.id}`;

const buildShareText = (review: Review) => {
  const heading = review.major && review.major.trim() ? review.major : review.title;
  const typeLabel = getReviewTypeConfig(review.type)?.label?.toLowerCase() ?? "experience";
  const lines: string[] = [];

  lines.push(`${heading} — ${review.organization} (${review.location})`);

  const start = monthYear(review.startDate);
  const end = review.startDate || review.endDate ? monthYear(review.endDate) ?? "Present" : null;
  if (start || end) lines.push([start ?? "?", end].filter(Boolean).join(" – "));

  lines.push("");
  lines.push(
    `Overall ${ratingWord(Number(review.rating))} ${typeLabel} (${Number(review.rating).toFixed(1)}/5) and ${difficultyWord(
      Number(review.difficulty)
    )} (${Number(review.difficulty).toFixed(1)}/5). ${review.wouldDoAgain ? "I'd do it again." : "I wouldn't do it again."}`
  );

  if (review.review?.trim()) {
    lines.push("");
    lines.push(`"${review.review.trim()}"`);
  }

  if (review.tags?.length) {
    lines.push("");
    lines.push(review.tags.join(" · "));
  }

  lines.push("");
  lines.push(`Full review on Vecurate → ${reviewUrl(review)}`);

  return lines.join("\n");
};

const copyToClipboard = async (text: string) => {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through to legacy path */
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.top = "0";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    ta.setSelectionRange(0, text.length);
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
};

const ShareReviewButton = ({ review }: Props) => {
  const [copied, setCopied] = useState(false);
  const [liOpen, setLiOpen] = useState(false);
  const [liLoading, setLiLoading] = useState(false);
  const [liText, setLiText] = useState("");

  const flashCopied = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCopy = async (mode: "text" | "link" = "text", override?: string) => {
    const payload = override ?? (mode === "link" ? reviewUrl(review) : buildShareText(review));
    const ok = await copyToClipboard(payload);
    if (ok) {
      flashCopied();
      toast({
        title: mode === "link" ? "Link copied" : "Copied",
        description: "Paste it anywhere you'd like to share.",
      });
    } else {
      toast({
        title: "Couldn't copy",
        description: "Your browser blocked clipboard access. Try selecting the text manually.",
        variant: "destructive",
      });
    }
    return ok;
  };

  const generateLinkedIn = async () => {
    setLiOpen(true);
    setLiLoading(true);
    setLiText("");
    try {
      const { data, error } = await supabase.functions.invoke("linkedin-post", {
        body: {
          type: review.type,
          title: review.title,
          major: review.major,
          organization: review.organization,
          location: review.location,
          rating: review.rating,
          difficulty: review.difficulty,
          wouldDoAgain: review.wouldDoAgain,
          tags: review.tags,
          review: review.review,
          responsibilities: review.responsibilities,
          interviewInfo: review.interviewInfo,
          startDate: review.startDate,
          endDate: review.endDate,
          url: reviewUrl(review),
        },
      });
      if (error || !data?.post) throw new Error(error?.message || "No post returned");
      setLiText(data.post as string);
    } catch (e) {
      toast({
        title: "Couldn't create the summary",
        description: "Please try again in a moment.",
        variant: "destructive",
      });
      setLiOpen(false);
    } finally {
      setLiLoading(false);
    }
  };

  const handleNativeShare = async () => {
    const heading = review.major && review.major.trim() ? review.major : review.title;
    try {
      await navigator.share({
        title: `${heading} — ${review.organization}`,
        text: buildShareText(review),
        url: reviewUrl(review),
      });
    } catch (err) {
      if ((err as DOMException)?.name === "AbortError") return;
      handleCopy("text");
    }
  };

  const canNativeShare = typeof navigator !== "undefined" && !!navigator.share;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            aria-label="Share this review"
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors"
          >
            {copied ? <Check className="w-3.5 h-3.5" /> : <Share2 className="w-3.5 h-3.5" />}
            {copied ? "Copied" : "Share"}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-[13rem]">
          <DropdownMenuItem onClick={() => handleCopy("text")} className="text-xs gap-2">
            <Copy className="w-3.5 h-3.5" />
            Copy review text
          </DropdownMenuItem>
          <DropdownMenuItem onClick={generateLinkedIn} className="text-xs gap-2">
            <Linkedin className="w-3.5 h-3.5" />
            Copy summary
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleCopy("link")} className="text-xs gap-2">
            <Link2 className="w-3.5 h-3.5" />
            Copy link
          </DropdownMenuItem>
          {canNativeShare && (
            <DropdownMenuItem onClick={handleNativeShare} className="text-xs gap-2">
              <Share2 className="w-3.5 h-3.5" />
              Share via…
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={liOpen} onOpenChange={setLiOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif">Shareable summary</DialogTitle>
            <DialogDescription className="text-xs">
              Polished and grammar-checked from your review. Edit it however you like before posting.
            </DialogDescription>
          </DialogHeader>

          {liLoading ? (
            <div className="flex items-center gap-2 py-10 justify-center text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" />
              Writing your post…
            </div>
          ) : (
            <Textarea
              value={liText}
              onChange={(e) => setLiText(e.target.value)}
              rows={14}
              className="text-sm leading-relaxed"
            />
          )}

          <div className="flex flex-wrap gap-2 justify-end">
            <Button variant="outline" size="sm" onClick={generateLinkedIn} disabled={liLoading}>
              Regenerate
            </Button>
            <Button
              size="sm"
              disabled={liLoading || !liText.trim()}
              onClick={async () => {
                const ok = await handleCopy("text", liText);
                if (ok) setLiOpen(false);
              }}
            >
              <Copy className="w-3.5 h-3.5 mr-1.5" />
              Copy summary
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ShareReviewButton;

